import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  doc, 
  getDoc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  deleteDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject, getBytes } from 'firebase/storage';
import { GoogleMap, useJsApiLoader, Marker, DrawingManager, InfoWindow } from '@react-google-maps/api';
import { kml } from '@tmcw/togeojson';
import { db, storage } from '../../firebase';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import LakeSpotDialog from './LakeSpotDialog';

const LAKE_TYPES = [
  'CAShallowStained',
  'CATroutWater',
  'GreatLakes',
  'MuskyRiver',
  'MNBigs',
  'SouthernReservoirs',
  'DeepClear',
  'ShallowStained'
];

const containerStyle = {
  width: '100%',
  height: '400px'
};

const libraries = ['drawing'];

function LakeDetail() {
  const { lakeId } = useParams();
  const navigate = useNavigate();
  const isNewLake = lakeId === 'new';
  const polygonRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [drawingMode, setDrawingMode] = useState(null);
  const [loading, setLoading] = useState(!isNewLake);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [polygonWarning, setPolygonWarning] = useState(null);
  
  // Lake Spots State
  const [spots, setSpots] = useState([]);
  const [isSpotDialogOpen, setIsSpotDialogOpen] = useState(false);
  const [currentSpot, setCurrentSpot] = useState(null);
  const [hoveredSpot, setHoveredSpot] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [spotToDelete, setSpotToDelete] = useState(null);
  const [addingSpotMode, setAddingSpotMode] = useState(false);
  const [uploadingKml, setUploadingKml] = useState(false);
  const [kmlFileName, setKmlFileName] = useState('');

  const [map, setMap] = useState(null);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries
  });

  const drawingManagerOptions = React.useMemo(() => {
    if (!mapLoaded) return null;
    return {
      drawingControl: false,
      polygonOptions: {
        fillColor: '#2196F3',
        fillOpacity: 0.5,
        strokeWeight: 2,
        clickable: false,
        editable: true,
        zIndex: 1,
      },
    };
  }, [mapLoaded]);

  const mapOptions = React.useMemo(() => ({
    draggableCursor: addingSpotMode ? 'pointer' : null,
  }), [addingSpotMode]);

  const [lake, setLake] = useState({
    name: '',
    stationId: '',
    lakeType: '',
    acres: '',
    published: false,
    latitude: '',
    longitude: '',
    promoCode: '',
    default: false,
    description: '',
    trophySizeIndex: '',
    sponsorAdId: '',
    sortOrder: 0,
    geoJson: '',
    kmlFile: ''
  });

  const fetchSpots = useCallback(async () => {
    try {
      const spotsRef = collection(db, 'lake-spots');
      const q = query(spotsRef, where('lakeId', '==', lakeId));
      const querySnapshot = await getDocs(q);
      const spotsData = [];
      querySnapshot.forEach((doc) => {
        spotsData.push({ id: doc.id, ...doc.data() });
      });
      setSpots(spotsData);
    } catch (err) {
      console.error("Error fetching spots:", err);
    }
  }, [lakeId]);

  useEffect(() => {
    const fetchLake = async () => {
      if (isNewLake) {
        // Don't fetch for new lakes, just set default coordinates
        setLake({
          name: '',
          stationId: '',
          lakeType: '',
          acres: '',
          published: false,
          latitude: 46.03978,
          longitude: -89.62341,
          promoCode: '',
          default: false,
          description: '',
          trophySizeIndex: '',
          sponsorAdId: '',
          sortOrder: 0,
          geoJson: '',
          kmlFile: ''
        });
        setLoading(false);
        return;
      }

      try {
        const docRef = doc(db, 'lakes', lakeId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const lakeData = { id: docSnap.id, ...docSnap.data() };
          setLake(lakeData);
          // Extract filename from kmlFile path if it exists
          if (lakeData.kmlFile) {
            const fileName = lakeData.kmlFile.split('/').pop();
            setKmlFileName(fileName);
          }
        } else {
          setError('Lake not found');
        }
      } catch (err) {
        console.error("Error fetching lake:", err);
        setError('Error fetching lake details');
      } finally {
        setLoading(false);
      }
    };

    if (lakeId) {
      fetchLake();
      if (!isNewLake) {
        fetchSpots();
      }
    }
  }, [lakeId, isNewLake, fetchSpots]);

  const handleOpenSpotDialog = (spot = null) => {
    setCurrentSpot(spot);
    setIsSpotDialogOpen(true);
  };

  const handleCloseSpotDialog = () => {
    setIsSpotDialogOpen(false);
    setCurrentSpot(null);
  };

  const handleSpotSave = async (spotData) => {
    try {
      if (spotData.id) {
        // Update
        const { id, ...data } = spotData;
        const spotRef = doc(db, 'lake-spots', id);
        await updateDoc(spotRef, data);
      } else {
        // Add
        await addDoc(collection(db, 'lake-spots'), {
          ...spotData,
          lakeId: lakeId
        });
      }
      fetchSpots();
      handleCloseSpotDialog();
    } catch (err) {
      console.error("Error saving spot:", err);
      setError('Error saving spot');
    }
  };

  const handleDeleteClick = (spot) => {
    setSpotToDelete(spot);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (spotToDelete) {
      try {
        await deleteDoc(doc(db, 'lake-spots', spotToDelete.id));
        fetchSpots();
      } catch (err) {
        console.error("Error deleting spot:", err);
        setError('Error deleting spot');
      }
      setDeleteConfirmOpen(false);
      setSpotToDelete(null);
      handleCloseSpotDialog();
    }
  };

  const handleMapClick = (e) => {
    if (addingSpotMode) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setAddingSpotMode(false);
      handleOpenSpotDialog({
        latitude: lat,
        longitude: lng
      });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setLake(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const onPolygonComplete = (polygon) => {
    // If there is already a polygon, remove it to keep only the new one
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
    }
    // Update ref to the new polygon
    polygonRef.current = polygon;

    const path = polygon.getPath();
    const vertexCount = path.getLength();
    const coordinates = [];
    
    // Google Maps API has limits on polygon complexity
    // Warn users if they're approaching or exceeding reasonable limits
    if (vertexCount > 500) {
      setPolygonWarning(`Warning: Polygon has ${vertexCount} points, which may exceed Google Maps limits. Consider simplifying the shape for better performance.`);
    } else if (vertexCount > 300) {
      setPolygonWarning(`Polygon has ${vertexCount} points. Complex polygons may have performance issues. Consider simplifying if possible.`);
    } else {
      setPolygonWarning(null);
    }
    
    // Get coordinates from the path
    for (let i = 0; i < path.getLength(); i++) {
      const latLng = path.getAt(i);
      coordinates.push([latLng.lng(), latLng.lat()]);
    }
    
    // Close the loop if needed (GeoJSON requires the first and last point to be the same for Polygons)
    if (coordinates.length > 0) {
      const first = coordinates[0];
      const last = coordinates[coordinates.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
        coordinates.push(first);
      }
    }

    const geoJson = {
      type: "Feature",
      properties: {},
      geometry: {
        type: "Polygon",
        coordinates: [coordinates]
      }
    };

    setLake(prev => ({
      ...prev,
      geoJson: JSON.stringify(geoJson, null, 2)
    }));
    
    // Remove the imperative polygon since we render it declaratively
    polygon.setMap(null);
    polygonRef.current = null;

    // Stop drawing after polygon is complete
    setDrawingMode(null);
  };

  const handleMarkerDragEnd = (e) => {
    const newLat = e.latLng.lat();
    const newLng = e.latLng.lng();
    setLake(prev => ({
      ...prev,
      latitude: newLat,
      longitude: newLng
    }));
  };

  // Add GeoJSON data to map when lake changes or KML file is uploaded
  useEffect(() => {
    const loadAndDisplayGeoData = async () => {
      if (!map) return;

      // Clear existing features
      map.data.forEach((feature) => {
        map.data.remove(feature);
      });

      // Priority 1: Load from KML file if it exists
      if (lake.kmlFile) {
        try {
          // Fetch KML file from storage using Firebase SDK
          const storageRef = ref(storage, lake.kmlFile);
          const bytes = await getBytes(storageRef);
          const kmlText = new TextDecoder().decode(bytes);
          
          // Parse KML to GeoJSON
          const parser = new DOMParser();
          const kmlDoc = parser.parseFromString(kmlText, 'text/xml');
          const geoJson = kml(kmlDoc);

          // Add GeoJSON to map
          map.data.addGeoJson(geoJson);

          // Set styling
          map.data.setStyle((feature) => {
            const properties = feature.getProperty('properties') || {};
            return {
              fillColor: properties.fillColor || '#2196F3',
              fillOpacity: properties.fillOpacity !== undefined ? properties.fillOpacity : 0.5,
              strokeColor: properties.strokeColor || '#2196F3',
              strokeOpacity: properties.strokeOpacity !== undefined ? properties.strokeOpacity : 0.8,
              strokeWeight: properties.strokeWeight !== undefined ? properties.strokeWeight : 2,
              clickable: false,
              editable: false,
              zIndex: 1,
            };
          });
        } catch (e) {
          console.error('Error loading KML file:', e);
        }
      } 
      // Priority 2: Load from manually drawn geoJson if no KML file
      else if (lake.geoJson) {
        try {
          const geoJson = JSON.parse(lake.geoJson);
          map.data.addGeoJson(geoJson);

          // Set styling
          map.data.setStyle((feature) => {
            const properties = feature.getProperty('properties') || {};
            return {
              fillColor: properties.fillColor || '#2196F3',
              fillOpacity: properties.fillOpacity !== undefined ? properties.fillOpacity : 0.5,
              strokeColor: properties.strokeColor || '#2196F3',
              strokeOpacity: properties.strokeOpacity !== undefined ? properties.strokeOpacity : 0.8,
              strokeWeight: properties.strokeWeight !== undefined ? properties.strokeWeight : 2,
              clickable: false,
              editable: false,
              zIndex: 1,
            };
          });
        } catch (e) {
          console.error('Error loading manually drawn geoJson:', e);
        }
      }
    };

    loadAndDisplayGeoData();
  }, [map, lake.kmlFile, lake.geoJson]);

  const handleClearPolygon = () => {
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }
    setPolygonWarning(null);
    setLake(prev => ({
      ...prev,
      geoJson: ''
    }));
  };

  const handleKmlUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file is KML
    if (!file.name.toLowerCase().endsWith('.kml')) {
      setError('Please upload a valid KML file');
      return;
    }

    if (isNewLake) {
      setError('Please save the lake first before uploading a KML file');
      return;
    }

    setUploadingKml(true);
    setError(null);

    try {
      // Read and parse KML file
      const fileText = await file.text();
      const parser = new DOMParser();
      const kmlDoc = parser.parseFromString(fileText, 'text/xml');
      
      // Convert KML to GeoJSON
      const geoJson = kml(kmlDoc);
      
      // Validate that we got valid GeoJSON
      if (!geoJson || !geoJson.type) {
        throw new Error('Failed to convert KML to GeoJSON');
      }

      // Create storage reference with pattern: lake-kmls/{lakeId}-{originalFileName}
      const fileName = `${lakeId}-${file.name}`;
      const storageRef = ref(storage, `lake-kmls/${fileName}`);

      // Delete old KML file if it exists
      if (lake.kmlFile) {
        try {
          const oldFileRef = ref(storage, lake.kmlFile);
          await deleteObject(oldFileRef);
        } catch (err) {
          console.warn('Could not delete old KML file:', err);
          // Continue with upload even if delete fails
        }
      }

      // Upload file
      await uploadBytes(storageRef, file);

      // Update lake record with KML file path only (geoJson will be loaded from storage)
      const kmlFilePath = `lake-kmls/${fileName}`;
      const docRef = doc(db, 'lakes', lakeId);
      await updateDoc(docRef, { 
        kmlFile: kmlFilePath
      });

      // Update local state
      setLake(prev => ({ ...prev, kmlFile: kmlFilePath }));
      setKmlFileName(file.name);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error uploading KML file:', err);
      setError('Error uploading KML file: ' + err.message);
    } finally {
      setUploadingKml(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleKmlDownload = async () => {
    if (!lake.kmlFile) return;

    try {
      const storageRef = ref(storage, lake.kmlFile);
      const downloadUrl = await getDownloadURL(storageRef);
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = kmlFileName || 'lake.kml';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading KML file:', err);
      setError('Error downloading KML file: ' + err.message);
    }
  };

  const handleKmlDelete = async () => {
    if (!lake.kmlFile) return;

    if (!window.confirm('Are you sure you want to delete the KML file? This action cannot be undone.')) {
      return;
    }

    try {
      // Delete from storage
      const storageRef = ref(storage, lake.kmlFile);
      await deleteObject(storageRef);

      // Update lake record
      const docRef = doc(db, 'lakes', lakeId);
      await updateDoc(docRef, { kmlFile: '' });

      // Update local state
      setLake(prev => ({ ...prev, kmlFile: '' }));
      setKmlFileName('');
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error deleting KML file:', err);
      setError('Error deleting KML file: ' + err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Prepare data for save
      const lakeData = {
        name: lake.name,
        lakeType: lake.lakeType,
        acres: Number(lake.acres) || 0,
        published: lake.published || false,
        latitude: Number(lake.latitude),
        longitude: Number(lake.longitude),
        promoCode: lake.promoCode || '',
        default: lake.default || false,
        description: lake.description || '',
        trophySizeIndex: Number(lake.trophySizeIndex) || 0,
        sponsorAdId: lake.sponsorAdId || '',
        sortOrder: Number(lake.sortOrder) || 0,
        kmlFile: lake.kmlFile || ''
      };

      // Only include geoJson for manually drawn polygons (not KML uploads)
      // and only if it's reasonably sized
      if (lake.geoJson && !lake.kmlFile) {
        const geoJsonSize = new Blob([lake.geoJson]).size;
        if (geoJsonSize < 1000000) { // Less than 1MB
          lakeData.geoJson = lake.geoJson;
        } else {
          console.warn('GeoJson too large, skipping save. Consider uploading as KML file instead.');
        }
      }

      if (isNewLake) {
        // Create new lake
        // StationId will be auto-assigned or generated
        lakeData.stationId = lake.stationId || `STATION_${Date.now()}`;
        const lakesRef = collection(db, 'lakes');
        const newDocRef = await addDoc(lakesRef, lakeData);
        setSuccess(true);
        // Navigate to the edit page of the newly created lake
        setTimeout(() => {
          navigate(`/lakes/${newDocRef.id}`);
        }, 1500);
      } else {
        // Update existing lake
        const docRef = doc(db, 'lakes', lakeId);
        await updateDoc(docRef, lakeData);
        setSuccess(true);
      }
    } catch (err) {
      console.error("Error saving lake:", err);
      setError(isNewLake ? 'Error creating lake' : 'Error updating lake');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !lake.id) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
     return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/lakes')} sx={{ mt: 2 }}>
          Back to Lakes
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <Button 
        startIcon={<ArrowBackIcon />} 
        onClick={() => navigate('/lakes')} 
        sx={{ mb: 2 }}
      >
        Back to Lakes
      </Button>
      
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          {isNewLake ? 'Add New Lake' : 'Edit Lake'}
        </Typography>

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {isNewLake ? 'Lake created successfully! Redirecting...' : 'Lake updated successfully!'}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {polygonWarning && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {polygonWarning}
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Polygon Drawing Tips:</strong>
          <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
            <li>Click on the map to add points to your polygon</li>
            <li>Double-click or click the first point to complete the shape</li>
            <li>For best performance, keep polygons under 300 points</li>
            <li>Google Maps may limit polygons to approximately 500 vertices</li>
            <li>If you need a complex shape, consider simplifying it or breaking it into multiple features</li>
          </ul>
        </Alert>

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Name"
              name="name"
              value={lake.name || ''}
              onChange={handleChange}
              fullWidth
              required
            />
            
            <TextField
              label="Station ID"
              name="stationId"
              value={lake.stationId || ''}
              onChange={handleChange}
              fullWidth
              disabled={!isNewLake}
              helperText={isNewLake ? "Optional: Leave empty to auto-generate" : "Station ID cannot be changed"}
            />

            <TextField
              label="Description"
              name="description"
              value={lake.description || ''}
              onChange={handleChange}
              fullWidth
              multiline
              rows={4}
            />
            
            <TextField
              select
              label="Lake Type"
              name="lakeType"
              value={lake.lakeType || ''}
              onChange={handleChange}
              fullWidth
            >
              {LAKE_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
            
            <TextField
              label="Acres"
              name="acres"
              type="number"
              value={lake.acres || ''}
              onChange={handleChange}
              fullWidth
            />

            <TextField
              label="Trophy Size Index"
              name="trophySizeIndex"
              type="number"
              value={lake.trophySizeIndex || ''}
              onChange={handleChange}
              fullWidth
            />

            <TextField
              label="Promo Code"
              name="promoCode"
              value={lake.promoCode || ''}
              onChange={handleChange}
              fullWidth
            />

            <TextField
              label="Sponsor Ad ID"
              name="sponsorAdId"
              value={lake.sponsorAdId || ''}
              onChange={handleChange}
              fullWidth
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Latitude"
                  name="latitude"
                  type="number"
                  value={lake.latitude || ''}
                  fullWidth
                  disabled
                  inputProps={{ step: "any" }}
                  helperText="Drag the marker on the map to update"
                />
                <TextField
                  label="Longitude"
                  name="longitude"
                  type="number"
                  value={lake.longitude || ''}
                  fullWidth
                  disabled
                  inputProps={{ step: "any" }}
                  helperText="Drag the marker on the map to update"
                />
            </Box>

            {!isNewLake && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Button
                  variant="contained"
                  startIcon={addingSpotMode ? <CloseIcon /> : <AddIcon />}
                  onClick={() => setAddingSpotMode(!addingSpotMode)}
                  color={addingSpotMode ? "error" : "primary"}
                >
                  {addingSpotMode ? "Cancel Adding Spot" : "Add New Spot"}
                </Button>
              </Box>
            )}

            <Box sx={{ height: '400px', width: '100%', mt: 2, mb: 2 }}>
              {isLoaded ? (
                <GoogleMap
                  mapContainerStyle={containerStyle}
                  center={{ 
                    lat: Number(lake.latitude) || 46.03978, 
                    lng: Number(lake.longitude) || -89.62341 
                  }}
                  zoom={12}
                  onLoad={(mapInstance) => {
                    setMapLoaded(true);
                    setMap(mapInstance);
                  }}
                  onUnmount={() => setMap(null)}
                  onClick={handleMapClick}
                  options={mapOptions}
                >
                  <Marker
                    draggable={true}
                    onDragEnd={handleMarkerDragEnd}
                    position={{ 
                      lat: Number(lake.latitude) || 46.03978, 
                      lng: Number(lake.longitude) || -89.62341 
                    }}
                  />
                  {mapLoaded && drawingManagerOptions && (
                    <DrawingManager
                      onPolygonComplete={onPolygonComplete}
                      options={drawingManagerOptions}
                      drawingMode={drawingMode}
                    />
                  )}
                  {!isNewLake && spots.map((spot) => (
                    <Marker
                      key={spot.id}
                      position={{ lat: spot.latitude, lng: spot.longitude }}
                      icon={{
                        url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png"
                      }}
                      onClick={() => handleOpenSpotDialog(spot)}
                      onMouseOver={() => setHoveredSpot(spot)}
                      onMouseOut={() => setHoveredSpot(null)}
                    />
                  ))}
                  {hoveredSpot && (
                    <InfoWindow
                      position={{ lat: hoveredSpot.latitude, lng: hoveredSpot.longitude }}
                      onCloseClick={() => setHoveredSpot(null)}
                      options={{
                        pixelOffset: new window.google.maps.Size(0, -30),
                        disableAutoPan: true
                      }}
                    >
                      <div style={{ padding: '4px' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{hoveredSpot.name}</Typography>
                        <Typography variant="caption" display="block">{hoveredSpot.typeDescription}</Typography>
                      </div>
                    </InfoWindow>
                  )}
                </GoogleMap>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress />
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => setDrawingMode('polygon')}
                disabled={drawingMode === 'polygon'}
              >
                Draw a Shape
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => setDrawingMode(null)}
                disabled={drawingMode === null}
              >
                Stop Drawing
              </Button>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                onClick={handleClearPolygon}
              >
                Clear Shape
              </Button>
            </Box>

            <TextField
              label="GeoJSON (Read Only)"
              name="geoJson"
              value={lake.geoJson || ''}
              multiline
              rows={6}
              fullWidth
              disabled
              InputProps={{
                readOnly: true,
                style: { fontFamily: 'monospace' }
              }}
              helperText="Draw a polygon on the map to generate GeoJSON"
            />

            {/* KML File Upload/Download Section */}
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                KML File
              </Typography>
              
              {!isNewLake ? (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    component="label"
                    startIcon={<UploadFileIcon />}
                    disabled={uploadingKml}
                  >
                    {uploadingKml ? 'Uploading...' : (lake.kmlFile ? 'Replace KML File' : 'Upload KML File')}
                    <input
                      type="file"
                      hidden
                      accept=".kml"
                      onChange={handleKmlUpload}
                    />
                  </Button>

                  {lake.kmlFile && (
                    <>
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleKmlDownload}
                      >
                        Download KML
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={handleKmlDelete}
                      >
                        Delete KML
                      </Button>
                      <Typography variant="body2" color="text.secondary">
                        Current file: {kmlFileName}
                      </Typography>
                    </>
                  )}

                  {!lake.kmlFile && (
                    <Typography variant="body2" color="text.secondary">
                      No KML file uploaded
                    </Typography>
                  )}
                </Box>
              ) : (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Save the lake first before uploading a KML file
                </Alert>
              )}
            </Box>
            
            <FormControlLabel
              control={
                <Switch
                  checked={lake.published || false}
                  onChange={handleChange}
                  name="published"
                  color="primary"
                />
              }
              label="Published"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={lake.default || false}
                  onChange={handleChange}
                  name="default"
                  color="primary"
                />
              }
              label="Default In app"
            />

            <TextField
              label="In App Sort Order"
              name="sortOrder"
              type="number"
              value={lake.sortOrder || ''}
              onChange={handleChange}
              fullWidth
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button 
                type="submit" 
                variant="contained" 
                disabled={loading}
              >
                {loading ? 'Saving...' : (isNewLake ? 'Create Lake' : 'Save Changes')}
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>

      <LakeSpotDialog
        open={isSpotDialogOpen}
        onClose={handleCloseSpotDialog}
        onSave={handleSpotSave}
        spot={currentSpot}
        lakeId={lakeId}
        lakeType={lake.lakeType}
        onDelete={handleDeleteClick}
      />

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Delete Spot</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{spotToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LakeDetail;
